import * as React from 'react';
import { Row, Col, Label, Button } from 'reactstrap';
import * as _ from 'lodash';
import { toastr } from 'react-redux-toastr';
import * as EmailValidator from 'email-validator';
import { History } from 'history';
import * as ReactGA from 'react-ga';
import { Form } from '../../../share/components/Form/Form';
import { CognitoLoginService } from '../../../share/services/cognito-login';
import { cognitoInfo } from '../../../share/config/cognito';
import { WrappedInput } from '../../../share/components/Input/Input';
import { isStrongEnough } from '../../../share/utils/passwordValidator';

export interface IForgotPasswordProps {
    history: History;
}

export interface IForgotPasswordState {
    email: string;
    loading: boolean;
    loadingMessage: string;
    verificationCode: string;
    newPassword: string;
    codeSent: boolean;
}

export class ForgotPassword extends React.Component<IForgotPasswordProps, IForgotPasswordState> {
    constructor(props: IForgotPasswordProps) {
        super(props);

        this.state = {
            email: "",
            loading: false,
            loadingMessage: "",
            verificationCode: "",
            newPassword: "",
            codeSent: false,
        };
    }

    public setValue = (key: string, value: any) => {
        const newState = { ...this.state };
        _.set(newState, key, value);
        this.setState(newState);
    }

    public onChange = (key) => (event) => {
        this.setValue(key, event.currentTarget.value);
    }

    public handleInvalidSubmit = () => {
        if (this.state.codeSent) {
            toastr.error('Forgot Password', 'Please fill all the required values.');

        } else {
            toastr.error('Forgot Password', 'Please provide a valid email.');
        }
    }

    public isFormValid(): string[] {
        const errors: string[] = [];
        const { email, codeSent, newPassword } = this.state;
        if (!EmailValidator.validate(email)) {
            errors.push("Please provide a valid email.");
        }
        if (codeSent) {
            if (!isStrongEnough(newPassword)) {
                errors.push("Password must be 10 characters long, have uppercase "
                    + "and lowercase letters, numbers and at least one special character: ?, -, *, +");
            }
        }
        return errors;
    }

    public handleValidSubmit = async () => {
        const errors = this.isFormValid();
        if (errors.length === 0) {
            try {
                let actionResult;
                if (this.state.codeSent) {
                    actionResult = await this.handleConfirmPassword();
                    if (actionResult.successful) {
                        toastr.success("Success", "Password has been changed!");
                        this.props.history.push('/login');
                    }
                } else {
                    actionResult = await this.handleSendCode();
                    if (actionResult.successful) {
                        this.setState({ loading: false, loadingMessage: '', codeSent: true, });
                    }
                }
                ReactGA.event({
                    category: 'Password',
                    action: `${this.state.codeSent ? 'Sent change code' : 'Changed user'} password`,
                    label: 'Password management'
                });
            } catch (error) {
                this.setState({ loading: false, loadingMessage: '' });
                toastr.error('Error', error.message);
            }
        } else {
            toastr.error('Error', errors.join("\n"));
        }
    }

    private handleSendCode = async (): Promise<any> => {
        this.setState({ loading: true, loadingMessage: 'Sending code' });
        const cognitoService = new CognitoLoginService(cognitoInfo);
        const forgotPasswordResult =
            await cognitoService.forgotPassword(this.state.email);
        return forgotPasswordResult;
    }

    private handleConfirmPassword = async (): Promise<any> => {
        this.setState({ loading: true, loadingMessage: 'Updating password' });
        const cognitoService = new CognitoLoginService(cognitoInfo);
        const { email, newPassword, verificationCode, } = this.state;
        const confirmPasswordResult =
            await cognitoService.confirmPassword(email, newPassword, verificationCode);
        return confirmPasswordResult;
    }

    public render() {
        const {
            email, loading,
            verificationCode, newPassword,
            codeSent,
        } = this.state;
        return (
            <Row>
                <Col md={12}>
                    <Form onInvalidSubmit={this.handleInvalidSubmit}
                        onValidSubmit={this.handleValidSubmit}>
                        {!codeSent && <Row>
                            <WrappedInput id="email" type="email"
                                name="email"
                                md={12}
                                placeholder="Enter email*"
                                value={email}
                                required={true}
                                className="input-field"
                                onChange={this.onChange('email')} />
                        </Row>}
                        {
                            codeSent &&
                            <>
                                <Row>
                                    <Label>Please check your email, a code has been sent to change the pasword.</Label>
                                </Row>
                                <Row>
                                    <WrappedInput id="verificationCode" type="text"
                                        name="verificationCode"
                                        md={12}
                                        placeholder="Verification Code"
                                        value={verificationCode}
                                        required={true}
                                        className="input-field"
                                        onChange={this.onChange('verificationCode')} />
                                </Row>
                                <Row>
                                    <WrappedInput id="newPassword" type="password"
                                        name="newPassword"
                                        md={12}
                                        placeholder="New Password"
                                        value={newPassword}
                                        required={true}
                                        className="input-field"
                                        onChange={this.onChange('newPassword')} />
                                </Row>
                            </>
                        }
                        <Row>
                            <Col md={6}>
                                <p className="required">*Required field</p>
                            </Col>
                            <Col md={6}>
                                <Button block={true} disabled={loading} type="submit">
                                    {!codeSent && "Send Code"}
                                    {codeSent && "Change Password"}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        );
    }
}

export default (ForgotPassword);


// WEBPACK FOOTER //
// ./src/modules/Auth/ForgotPassword/ForgotPassword.tsx